import { Bell, Check, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@lib/utils';
import {
  useNotifications,
  useUnreadNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
} from '@core/hooks/useNotifications';
import { Button } from '@components/ui/Button';
import { Badge } from '@components/ui/Badge';

interface NotificationBellProps {
  className?: string;
}

function useNotificationsPath() {
  const location = useLocation();
  if (location.pathname.startsWith('/portal')) return '/portal/notificaciones';
  if (location.pathname.startsWith('/technician')) return '/technician/notificaciones';
  return '/admin/notificaciones';
}

export function NotificationBell({ className }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const notificationsPath = useNotificationsPath();
  const { data: unreadCount = 0 } = useUnreadNotifications();
  const { data: notifications = [], isLoading } = useNotifications(20, false);
  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();
  const deleteMutation = useDeleteNotification();

  const handleMarkAsRead = (notificationId: string) => {
    markAsReadMutation.mutate(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleDelete = (notificationId: string) => {
    deleteMutation.mutate(notificationId);
  };

  const getPriorityColor = (prioridad?: string) => {
    switch (prioridad) {
      case 'urgente':
        return 'text-danger-500 bg-danger-50';
      case 'alta':
        return 'text-warning-500 bg-warning-50';
      case 'media':
        return 'text-accent-500 bg-accent-50';
      default:
        return 'text-gray-500 bg-gray-50';
    }
  };

  return (
    <div className={cn('relative', className)}>
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative rounded-full p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
        aria-label="Notificaciones"
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-danger-500 text-[10px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="absolute right-0 top-12 z-50 w-96 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 p-4">
              <h3 className="font-heading text-lg font-semibold text-gray-900">
                Notificaciones
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-sm text-accent-600 hover:text-accent-700 font-medium"
                    disabled={markAllAsReadMutation.isPending}
                  >
                    Marcar todas como leídas
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-[400px] overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-merino-500 border-t-transparent" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="py-12 text-center">
                  <Bell className="mx-auto h-12 w-12 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">
                    No tienes notificaciones
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={cn(
                        'group relative p-4 hover:bg-gray-50 transition-colors',
                        !notification.leida && 'bg-blue-50'
                      )}
                    >
                      {/* Prioridad Badge */}
                      {notification.prioridad && notification.prioridad !== 'baja' && (
                        <Badge
                          variant="custom"
                          className={cn(
                            'absolute top-2 right-2 text-xs',
                            getPriorityColor(notification.prioridad)
                          )}
                        >
                          {notification.prioridad}
                        </Badge>
                      )}

                      {/* Content */}
                      <div className="pr-16">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {notification.titulo}
                        </h4>
                        <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                          {notification.mensaje}
                        </p>
                        <p className="mt-2 text-xs text-gray-400">
                          {formatDistanceToNow(new Date(notification.fecha_creacion), {
                            addSuffix: true,
                            locale: es,
                          })}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!notification.leida && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="rounded-md p-1.5 text-accent-600 hover:bg-accent-100"
                            title="Marcar como leída"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(notification.id)}
                          className="rounded-md p-1.5 text-danger-600 hover:bg-danger-100"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="border-t border-gray-200 p-3 text-center">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate(notificationsPath);
                  }}
                  className="text-sm font-medium text-accent-600 hover:text-accent-700"
                >
                  Ver todas las notificaciones
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

